/**
 * What a Hide and Seek map shows: markers on one map image plus a list of the
 * same players, built from the round and the locations a viewer was sent.
 *
 * The ONE builder for every surface — the Electron main window, the map overlay
 * and admin-web — so they can never disagree about who is drawn where. It is a
 * pure projection: privacy is decided on the server before any of its inputs
 * arrive (players never receive exact hider positions, and only see seekers when
 * the round allows), so this draws whatever it is given.
 *
 * Marco Polo circles come in pings: every hider answers each Marco Polo once,
 * and the round keeps every circle shown (`approxHistory`). A view shows the
 * latest circle per hider, one ping's circles, or all of them with a trail
 * through each hider's circles in order.
 *
 * A round is played on its planet's map or, when the admin chose one, on one
 * area's map (Duros Sector). An area round shows only that map: a hider whose
 * circle falls outside the area is listed, never drawn, and the list can open
 * the map they are on.
 */
import { getPlayerDisplayName, isHideSeekRoundPlayer, isHnsPlayerDisqualified, isHnstRoundPlayer } from '../types/player';
import { isWithinMap, radiusToMapPercent, worldToMapPercent } from './mapGeometry';
/** Whole-metre coordinates, as players read them off the in-game map. */
function formatCoords(x, y, z) {
    return `${Math.round(x)}, ${Math.round(y)}, ${Math.round(z)}`;
}
/** Hide and Seek Teams players are drawn in their team's colour; Hide and Seek players by role. */
function toneFor(player) {
    return isHnstRoundPlayer(player) ? player.team : player.role;
}
/** The team, for a Hide and Seek Teams player's row. */
function teamOf(player) {
    return isHnstRoundPlayer(player) ? { team: player.team } : {};
}
/**
 * Whether a player is out of the round: disqualified, or in Hide and Seek
 * Teams caught (a caught Hide and Seek hider becomes a seeker instead).
 */
function isOut(player) {
    return isHnsPlayerDisqualified(player) || (isHnstRoundPlayer(player) && player.capturedAt != null);
}
/** Every circle a hider was shown, oldest first. */
function historyOf(player) {
    if (player.approxHistory?.length)
        return player.approxHistory;
    return player.approxPosition ? [player.approxPosition] : [];
}
/** When each Marco Polo event of the round was first answered. */
function firstAnswered(round) {
    const firstAt = new Map();
    for (const player of round.players) {
        if (!isHideSeekRoundPlayer(player))
            continue;
        for (const approx of historyOf(player)) {
            if (approx.eventId === null)
                continue;
            const at = firstAt.get(approx.eventId);
            if (at === undefined || approx.at < at)
                firstAt.set(approx.eventId, approx.at);
        }
    }
    return firstAt;
}
/**
 * The round's Marco Polo pings, in order: each event any hider answered, by
 * when it was first answered. A circle's ping is its event's place here (1-based).
 */
export function hnsMarcoPoloPings(round) {
    return [...firstAnswered(round).entries()].sort((a, b) => a[1] - b[1]).map(([eventId]) => eventId);
}
/**
 * A circle's ping: its event's, or for an answer outside any Marco Polo the
 * latest ping by then (at least 1).
 */
function pingOf(approx, pings, firstAt) {
    if (approx.eventId !== null) {
        const index = pings.indexOf(approx.eventId);
        if (index >= 0)
            return index + 1;
    }
    return Math.max(1, pings.filter((eventId) => (firstAt.get(eventId) ?? Infinity) <= approx.at).length);
}
/**
 * Whether a circle is outside an area round's area: on another of the planet's
 * maps, or off the area's map. A map lying wholly inside the area's map (an
 * interior the game files under the planet) counts as the area. False on a
 * planet round, and for a hider in another area altogether (listed as off the
 * round's planet instead).
 */
export function isOutsideRoundArea(approx, location) {
    const area = location.zone;
    if (!area || approx.inRoundArea === false)
        return false;
    if (approx.zoneMapId == null)
        return !isWithinMap(area.bounds, approx.x, approx.y);
    if (approx.zoneMapId === area.id)
        return false;
    const map = location.zones.find((zone) => zone.id === approx.zoneMapId);
    return !map || !containsBounds(area.bounds, map.bounds);
}
function containsBounds(outer, inner) {
    return inner.minX >= outer.minX && inner.maxX <= outer.maxX && inner.minY >= outer.minY && inner.maxY <= outer.maxY;
}
export function buildHnsMapView(input) {
    const { round, map, location, players, locations = [], self, showExact = false, latestMarcoPoloAt = null, ping = 'latest' } = input;
    const now = input.now ?? Date.now();
    const markers = [];
    const rows = [];
    const trails = [];
    const place = (x, y) => worldToMapPercent(map.bounds, x, y);
    const age = (at) => Math.max(0, Math.round((now - at) / 1000));
    const firstAt = firstAnswered(round);
    const pings = [...firstAt.entries()].sort((a, b) => a[1] - b[1]).map(([eventId]) => eventId);
    for (const player of round.players) {
        if (!isHideSeekRoundPlayer(player))
            continue;
        const name = getPlayerDisplayName(player.playerId, players);
        const out = isOut(player);
        // Hiders found by Marco Polo: the approximate circles (everyone) ...
        if (player.role === 'hider') {
            const history = historyOf(player);
            const latest = history[history.length - 1];
            const shown = ping === 'all'
                ? history
                : ping === 'latest'
                    ? (latest ? [latest] : [])
                    : history.filter((approx) => pingOf(approx, pings, firstAt) === ping).slice(-1);
            const trail = [];
            shown.forEach((approx, index) => {
                // A hider who answered from another planet isn't on this one's maps:
                // listed, never drawn (map bounds of different planets overlap). Nor
                // is one outside an area round's area.
                const inArea = approx.inRoundArea !== false;
                const outsideArea = location ? isOutsideRoundArea(approx, location) : false;
                const markerId = ping === 'all' ? `approx:${player.playerId}:${index}` : `approx:${player.playerId}`;
                const centre = place(approx.x, approx.y);
                const size = radiusToMapPercent(map.bounds, approx.radius);
                // In one ping's view, every circle is that ping's; otherwise the
                // latest Marco Polo decides what is old.
                const stale = typeof ping === 'number'
                    ? false
                    : approx !== latest || (latestMarcoPoloAt !== null && approx.at < latestMarcoPoloAt);
                if (inArea && !outsideArea && centre && size) {
                    markers.push({
                        id: markerId, kind: 'circle', tone: toneFor(player), ...centre, ...size, label: name, stale,
                        ...(out && { out: true }),
                    });
                    trail.push(centre);
                }
                rows.push({
                    playerId: player.playerId, name, role: 'hider', ...teamOf(player), kind: 'approx',
                    position: { x: approx.x, y: approx.y }, radius: approx.radius,
                    ageSeconds: age(approx.at),
                    onMap: inArea && !outsideArea && isWithinMap(map.bounds, approx.x, approx.y), stale, out, markerId,
                    ping: pingOf(approx, pings, firstAt), zoneMapId: approx.zoneMapId ?? null,
                    ...(outsideArea && { outsideArea: true }), localMap: approx.localMap ?? null,
                });
            });
            if (ping === 'all' && trail.length > 1)
                trails.push({ id: `trail:${player.playerId}`, tone: toneFor(player), points: trail });
        }
        // ... and the exact spot (admins only).
        if (showExact && player.role === 'hider' && player.coordinates) {
            const { x, y, z } = player.coordinates;
            const at = place(x, y);
            const markerId = `exact:${player.playerId}`;
            if (at) {
                markers.push({
                    id: markerId, kind: 'dot', tone: toneFor(player), ...at, label: name, sublabel: formatCoords(x, y, z),
                    ...(out && { out: true }),
                });
            }
            rows.push({
                playerId: player.playerId, name, role: 'hider', ...teamOf(player), kind: 'exact',
                position: { x, y, z }, ageSeconds: player.approxPosition ? age(player.approxPosition.at) : 0,
                onMap: isWithinMap(map.bounds, x, y), stale: false, out, markerId,
            });
        }
    }
    // Live positions: whatever the viewer was sent, labelled by current role (or team).
    for (const location of locations) {
        if (self && location.playerId === self.playerId)
            continue;
        const roundPlayer = round.players.find((p) => p.playerId === location.playerId);
        if (!roundPlayer || !isHideSeekRoundPlayer(roundPlayer))
            continue;
        const { x, y, z } = location.position;
        const name = getPlayerDisplayName(location.playerId, players);
        const out = isOut(roundPlayer);
        const markerId = `live:${location.playerId}`;
        const at = place(x, y);
        if (at) {
            markers.push({
                id: markerId,
                kind: 'dot',
                tone: toneFor(roundPlayer),
                ...at,
                label: name,
                sublabel: formatCoords(x, y, z),
                ...(out && { out: true }),
            });
        }
        rows.push({
            playerId: location.playerId, name, role: roundPlayer.role, ...teamOf(roundPlayer), kind: 'live',
            position: { x, y, z }, ageSeconds: age(location.positionAt),
            onMap: isWithinMap(map.bounds, x, y), stale: false, out, markerId,
        });
    }
    if (self) {
        const { x, y, z } = self.position;
        const at = place(x, y);
        if (at)
            markers.push({ id: 'self', kind: 'dot', tone: 'self', ...at, label: 'You', sublabel: formatCoords(x, y, z) });
    }
    return { markers, rows, trails };
}
/**
 * The one map a round is shown on: its area's map in an area round, else its
 * planet's. Every view opens on it, and a view that can't be clicked (the map
 * overlay) never leaves it.
 */
export function hnsRoundMap(location) {
    return location.zone ?? location.world;
}
